import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, Briefcase, User, FileText, MessageCircle, Users, ClipboardList, Brain, Target, BookOpen, Download, Calculator, BarChart3, Globe, Laptop, Link2, Compass } from "lucide-react";
import BottomNavSheet from "./BottomNavSheet";
import { LeadFormModal } from "../modals/LeadFormModal";

// Soluciones - matches NavLinks
const solucionesItems = [
  { title: "Reclutamiento y Selección", path: "/reclutamiento-seleccion", icon: Users, description: "Headhunting especializado" },
  { title: "Pruebas Psicométricas", path: "/pruebas-psicometricas", icon: Brain, description: "Evaluación de talento" },
  { title: "Talent Connect", path: "/talent-connect", icon: Link2, description: "Base de talento ejecutivo" },
  { title: "Encuesta Salarial", path: "/encuesta-salarial", icon: BarChart3, description: "Benchmarks del mercado" },
];

// Productos - matches NavLinks
const productosItems = [
  { title: "Nexus ATS", path: "/nexus-ats", icon: Laptop, description: "Sistema de seguimiento" },
  { title: "Elempleo.com", path: "/elempleo", icon: Globe, description: "Portal de empleo #1" },
  { title: "Calculadora Salarial", path: "/calculadora-salarial", icon: Calculator, description: "Calcula tu salario ideal" },
];

// Candidatos - matches NavLinks
const candidatosItems = [
  { title: "Registrar CV", path: "/registrar-cv", icon: ClipboardList, description: "Únete a nuestra base" },
  { title: "Ofertas de Empleo", path: "/ofertas-empleo", icon: Briefcase, description: "Oportunidades laborales" },
  { title: "Nexus Lab", path: "/nexus-lab", icon: Brain, description: "Creador de CV con IA" },
  { title: "Orientación Vocacional", path: "/orientacion-vocacional", icon: Compass, description: "Descubre tu carrera ideal" },
];

// Recursos - matches NavLinks
const recursosItems = [
  { title: "Descargables", path: "/descargables", icon: Download, description: "Guías y plantillas gratuitas" },
  { title: "Blog", path: "https://blog.nexushr.tech/", icon: BookOpen, description: "Artículos y tendencias HR" },
];

// Combined for "Empresas" bottom nav (Soluciones + Productos)
const empresasItems = [...solucionesItems, ...productosItems];

const BottomNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSheet, setActiveSheet] = useState<string | null>(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  const solucionesRoutes = solucionesItems.map(i => i.path);
  const productosRoutes = productosItems.map(i => i.path);
  const candidatosRoutes = candidatosItems.map(i => i.path);
  const recursosRoutes = ["/descargables"];

  const isActive = (paths: string[]) => paths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));

  const navItems = [
    { 
      id: "home", 
      icon: Home, 
      label: "Inicio", 
      action: () => navigate("/"),
      isActive: location.pathname === "/"
    },
    { 
      id: "empresas", 
      icon: Briefcase, 
      label: "Empresas", 
      action: () => setActiveSheet("empresas"),
      isActive: isActive([...solucionesRoutes, ...productosRoutes])
    },
    { 
      id: "candidatos", 
      icon: User, 
      label: "Candidatos", 
      action: () => setActiveSheet("candidatos"),
      isActive: isActive(candidatosRoutes)
    },
    { 
      id: "recursos", 
      icon: FileText, 
      label: "Recursos", 
      action: () => setActiveSheet("recursos"),
      isActive: isActive(recursosRoutes)
    },
    { 
      id: "contacto", 
      icon: MessageCircle, 
      label: "Contacto", 
      action: () => setContactModalOpen(true),
      isActive: false
    },
  ];

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-[100] bg-white/95 backdrop-blur-md shadow-[0_-2px_10px_rgba(0,0,0,0.1)] md:hidden safe-area-pb">
        <div className="flex items-center justify-around h-16 px-2">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={item.action}
              className={`flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-colors relative ${
                item.isActive 
                  ? "text-primary" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <item.icon className={`w-5 h-5 ${item.isActive ? "stroke-[2.5]" : ""}`} />
              <span className="text-[10px] font-medium">{item.label}</span>
              {item.isActive && (
                <div className="absolute bottom-1 w-1 h-1 rounded-full bg-primary" />
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* Sheets */}
      <BottomNavSheet
        isOpen={activeSheet === "empresas"}
        onClose={() => setActiveSheet(null)}
        title="Para Empresas"
        items={empresasItems}
        sections={[
          { title: "Soluciones", items: solucionesItems },
          { title: "Productos", items: productosItems },
        ]}
      />
      <BottomNavSheet
        isOpen={activeSheet === "candidatos"}
        onClose={() => setActiveSheet(null)}
        title="Para Candidatos"
        items={candidatosItems}
      />
      <BottomNavSheet
        isOpen={activeSheet === "recursos"}
        onClose={() => setActiveSheet(null)}
        title="Recursos"
        items={recursosItems}
      />

      {/* Contact Modal */}
      <LeadFormModal
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        route="/contacto"
      />
    </>
  );
};

export default BottomNavigation;
